// Infinite scroll - v3.0
//
// Exemple avec toutes les options (toutes facultatives, valeurs par défaut ci-dessous) :
//
//   <infinite-scroll
//     auto-load="true"
//     load-more-text="Voir plus de produits"
//     loading-image="{{ 'loader.gif' | asset_url }}"
//     threshold="-100"
//     show-progress="false"
//     product-count-selector=".mobile-facets__count"
//     append-chunk-size="8"
//     section-rendering="true"
//     content-visibility="true"
//   />
//
// Les valeurs sont du Liquid comme le reste du template : un asset du thème se passe avec
// {{ 'mon-loader.gif' | asset_url }}, une image de réglage avec {{ settings.loader | image_url }},
// une URL de CDN en dur fonctionne aussi.

if (!customElements.get("infinite-scroll")) {
  const APPENDING_CLASS = "infinite-scroll-appending";
  const CONTENT_VISIBILITY_CLASS = "infinite-scroll-cv";
  class InfiniteScrollComponent extends HTMLElement {
    constructor() {
      super();
      this.currentPage = 1;
      this.isLoading = false;
      this.hasMorePages = true;
      this.loadedProductsCount = 0;
      this.totalProductsCount = 0;
      this.gridObserver = null;
      this.lastProductsSignature = null;
      this._cacheJob = null;
      this._htmlCacheDisabled = false;
      this._disconnected = false;
      this._restorePending = false;
      this._loadGeneration = 0;
      // Options à modifier selon les préférences
      this.options = {
        // Charge la page suivante au scroll ; false affiche un bouton "voir plus".
        autoLoad: true,
        // Libellé de ce bouton, donc sans effet tant que autoLoad est true. Traduisible en utilisant une variable js initialisée dans theme.liquid par exemple.
        loadMoreText: "Load More Products",
        // Visuel affiché pendant un chargement
        loadingImageUrl: "https://upload.wikimedia.org/wikipedia/commons/9/92/Loading_icon_cropped.gif",
        // Distance au bas de la grille qui déclenche le chargement en px.
        scrollThreshold: -100,
        // Compteur de produits et barre de progression, en mode bouton uniquement.
        showProgress: false,

        // Le conteneur des cartes produit.
        productsContainer: "#product-grid",
        // Une carte produit à l'intérieur de ce conteneur.
        productSelector: ".grid__item",
        // La pagination du thème, masquée au démarrage.
        paginationSelector: ".pagination-wrapper",
        // Où lire le total de produits ; sert uniquement à la barre de progression.
        productCountSelector: ".mobile-facets__count",

        // Produits insérés à la fois : sans effet sur le products per page, réduit le lag.
        appendChunkSize: 8,
        // Ne demander que la section ; vérifier que sa réponse contient bien la pagination.
        useSectionRendering: false,
        // Allège une grille très longue ; mesurer contain-intrinsic-height (CSS) avant d'activer.
        contentVisibility: false,
        // Charge sans attendre le 1er visuel des cartes insérées ; requis si contentVisibility.
        eagerAppendedImages: true,
        // Attente maximale des visuels avant de dévoiler un paquet de cartes, en ms.
        revealImageTimeout: 1200,

        // Préfixe des clés sessionStorage du composant.
        storagePrefix: "InfiniteScroll_",
        // Plafond du HTML mis en cache ; quota sessionStorage ~5 Mo par origine max.
        htmlCacheMaxChars: 2200000,
        // Pages refetchées au maximum au retour sur une collection déjà parcourue.
        maxCatchUpPages: 20,
      };
    }

    connectedCallback() {
      if (!window.location.pathname.includes("/collections/")) return;
      if ("scrollRestoration" in history) history.scrollRestoration = "manual";
      [
        ["auto-load", (val) => (this.options.autoLoad = val !== "false")],
        ["load-more-text", (val) => (this.options.loadMoreText = val)],
        ["loading-image", (val) => (this.options.loadingImageUrl = val)],
        ["threshold", (val) => (this.options.scrollThreshold = parseInt(val) || 0)],
        ["show-progress", (val) => (this.options.showProgress = val === "true")],
        ["product-count-selector", (val) => (this.options.productCountSelector = val)],
        ["append-chunk-size", (val) => (this.options.appendChunkSize = Math.max(1, parseInt(val) || this.options.appendChunkSize))],
        ["section-rendering", (val) => (this.options.useSectionRendering = val === "true")],
        ["content-visibility", (val) => (this.options.contentVisibility = val === "true")],
            ].forEach(([attributeName, setOption]) =>
        this.hasAttribute(attributeName) && setOption(this.getAttribute(attributeName))
      );
      if (!this.initializeState(true)) return;
      this.setupUI();
      if (this.options.autoLoad) {
        this.boundCheckScroll = () => !this._suppressAutoLoad && this.productsContainer?.getBoundingClientRect().bottom - this.options.scrollThreshold <= window.innerHeight && this.loadNextPage();
        window.addEventListener("scroll", this.boundCheckScroll);
      }
      this.boundSaveOnHide = () => { this.saveAnchor(); this.flushHtmlCache(); };
      window.addEventListener("pagehide", this.boundSaveOnHide);
      this.boundSaveOnHidden = () => { if (document.visibilityState === "hidden") { this.saveAnchor(); this.flushHtmlCache(); } };
      document.addEventListener("visibilitychange", this.boundSaveOnHidden);
      this.boundHandlePageShow = () => { if (!this._restorePending) this.restoreScrollPosition(); };
      window.addEventListener("pageshow", this.boundHandlePageShow);
      this.startRestore(); // HTML caché réinjecté + rattrapage des pages manquantes
      this.observeGridChanges();
    }

    initializeState(useCache = false) {
      this.productsContainer = document.querySelector(this.options.productsContainer);
      this.pagination = document.querySelector(this.options.paginationSelector);
      if (!this.productsContainer) return false;
      if (this.options.contentVisibility) this.productsContainer.classList.add(CONTENT_VISIBILITY_CLASS);
      const productCountElement = document.querySelector(this.options.productCountSelector);
      if (productCountElement) {
        const text = productCountElement.textContent.trim();
        const match = text.match(/\d+/);
        this.totalProductsCount = match ? parseInt(match[0]) : 0;
      }
      const cached = useCache && this.cache("get");
      if (cached?.url === window.location.href) {
        this.productsContainer.innerHTML = cached.html;
        this.cleanSwiperTraces();
        this.currentPage = cached.currentPage;
        this.hasMorePages = cached.hasMorePages;
        this.loadedProductsCount = this.productsContainer.querySelectorAll(this.options.productSelector).length;
      } else {
        this.currentPage = 1;
        this.hasMorePages = this.checkHasMorePages(document);
        this.loadedProductsCount = this.productsContainer.querySelectorAll(this.options.productSelector).length;
      }
      // Le conteneur vient d'être re-requêté : l'observateur de l'ancien élément est mort avec lui.
      if (this.gridObserver) this.observeGridChanges();
      return true;
    }

    setupUI() {
      if (!this.pagination) return;
      this.pagination.style.display = "none";
      this.removeLoadingElements();
      if (!this.options.autoLoad && this.hasMorePages) {
        this.loadMoreWrapper = this.createElementAfter(this.productsContainer, "div", { class: "infinite-scroll-load-more-wrapper" });
        if (this.options.showProgress) {
          this.productCountText = this.createAndAppend(this.loadMoreWrapper, "p", { class: "infinite-scroll-product-count" });
          this.progressBarBackground = this.createAndAppend(this.loadMoreWrapper, "div", { class: "infinite-scroll-progress-bar-background" });
          this.progressBar = this.createAndAppend(this.progressBarBackground, "div", { class: "infinite-scroll-progress-bar" });
        }
        this.loadMoreBtn = this.createAndAppend(this.loadMoreWrapper, "button", { class: "infinite-scroll-load-more", text: this.options.loadMoreText, onclick: () => this.loadNextPage() });
        this.updateLoadingState(false);
      }
      if (this.options.loadingImageUrl) {
        this.loadingImgContainer = this.createElementAfter(this.loadMoreWrapper || this.productsContainer, "div", {class: "infinite-scroll-loading-image-container"});
        this.loadingImg = this.createAndAppend(this.loadingImgContainer, "img", {class: "infinite-scroll-loading-image", src: this.options.loadingImageUrl, alt: "Loading image", style: "display:none;"});
      }
    }

    getProducts() {
      return this.productsContainer ? Array.from(this.productsContainer.querySelectorAll(this.options.productSelector)) : [];
    }

    rememberTargetPage() {
      this.cache("set", { targetPage: this.currentPage, targetUrl: window.location.href });
    }

    startRestore() {
      const anchorIndex = this.cache("get", "anchorIndex");
      const anchorReady = Number.isFinite(anchorIndex) && anchorIndex >= 0 && anchorIndex < this.loadedProductsCount;
      if (anchorReady) this.restoreScrollPosition();
      else this._restorePending = true; // `pageshow` ne doit pas repositionner avant le rattrapage
      this.catchUpToTargetPage().then(() => {
        if (!this._restorePending) return;
        this._restorePending = false;
        this.restoreScrollPosition();
      });
    }

    async catchUpToTargetPage() {
      if (this.isLoading) return false;
      if (this.cache("get", "targetUrl") !== window.location.href) return false;
      const target = parseInt(this.cache("get", "targetPage"), 10);
      if (!Number.isFinite(target) || target <= this.currentPage) return false;
      const lastPage = Math.min(target, this.currentPage + this.options.maxCatchUpPages);
      this.isLoading = true; // bloque aussi l'autoload au scroll (loadNextPage sort si isLoading)
      const gen = this._loadGeneration;
      this.updateLoadingState(true);
      try {
        while (this.currentPage < lastPage && this.hasMorePages && !this.isStale(gen)) {
          if (!(await this.fetchPage(this.currentPage + 1))) break;
        }
        if (!this.hasMorePages) this.handleEndOfResults();
        if (lastPage === target) this.rememberTargetPage();
        this.scheduleHtmlCache();
      } catch {
      } finally {
        this.revealPending();
        if (gen === this._loadGeneration) { // même verrou que loadNextPage
          this.isLoading = false;
          this.updateLoadingState(false);
        }
      }
      return true;
    }

    saveAnchor() {
      const products = this.getProducts();
      let anchorIndex = -1, anchorOffset = 0;
      for (let i = 0; i < products.length; i++) {
        const rect = products[i].getBoundingClientRect();
        // On mémorise quel produit était en haut, pas un pixel : un pixel dérive au chargement
        // des images situées au-dessus.
        if (rect.bottom > 0) { anchorIndex = i; anchorOffset = Math.round(rect.top); break; } // 1er produit visible
      }
      this.cache("set", { scrollUrl: window.location.href, anchorIndex, anchorOffset });
    }

    restoreScrollPosition() {
      const savedUrl = this.cache("get", "scrollUrl");
      if (savedUrl !== window.location.href) return; // pas la même collection -> on ne touche à rien
      const anchorIndex = this.cache("get", "anchorIndex");
      const anchorOffset = this.cache("get", "anchorOffset") || 0;
      // Uniquement sur un produit présent : une position en pixels mémorisée dépasserait la
      // hauteur d'une grille plus courte, et le scrollTo écrêté atterrirait tout en bas.
      if (!Number.isFinite(anchorIndex) || anchorIndex < 0) return;
      if (!this.getProducts()[anchorIndex]) return;

      this._suppressAutoLoad = true;
      let stopped = false, ro = null, stopTimer = null;
      const onLoadApply = () => apply();
      const stop = () => {
        if (stopped) return;
        stopped = true;
        this._suppressAutoLoad = false;
        window.removeEventListener("wheel", onUserScroll);
        window.removeEventListener("touchmove", onUserScroll);
        window.removeEventListener("keydown", onUserScroll);
        window.removeEventListener("load", onLoadApply);
        if (ro) ro.disconnect();
        clearTimeout(stopTimer);
      };
      const onUserScroll = () => stop(); // un vrai geste utilisateur annule la restauration

      const computeTarget = () => {
        const anchor = this.getProducts()[anchorIndex];
        return anchor ? anchor.getBoundingClientRect().top + window.scrollY - anchorOffset : null;
      };
      const apply = () => {
        if (stopped) return;
        const raw = computeTarget();
        if (raw === null) { stop(); return; }
        const target = Math.max(0, raw);
        const maxReach = document.documentElement.scrollHeight - window.innerHeight;
        // Ne scroller que si la page peut atteindre la cible, sinon le navigateur écrête vers le bas.
        if (maxReach >= target - 4 && Math.abs(window.scrollY - target) > 2) window.scrollTo(0, target);
      };

      if (window.ResizeObserver && this.productsContainer) {
        ro = new ResizeObserver(() => apply());
        ro.observe(this.productsContainer);
      }
      let frames = 0;
      const tick = () => { if (stopped) return; apply(); if (++frames < 75) requestAnimationFrame(tick); };
      if (document.readyState !== "complete") window.addEventListener("load", onLoadApply, { once: true });
      window.addEventListener("wheel", onUserScroll, { passive: true, once: true });
      window.addEventListener("touchmove", onUserScroll, { passive: true, once: true });
      window.addEventListener("keydown", onUserScroll, { once: true });
      stopTimer = setTimeout(stop, 2500); // borne dure : on arrête de corriger après 2.5s
      apply();
      requestAnimationFrame(tick);
    }

    saveHtmlCache() {
      if (this._htmlCacheDisabled || !this.productsContainer) return;
      this.revealPending(); // jamais de classe de masquage dans le snapshot
      this.restoreLazyLoading(); // ni 900 images `eager` : voir restoreLazyLoading
      const html = this.productsContainer.innerHTML;
      // ORDRE CRITIQUE : le HTML d'abord, les métadonnées seulement si son écriture a réussi,
      // sinon on stocke currentPage=10 avec le HTML de la page 5 et des pages sont sautées.
      if (html.length > this.options.htmlCacheMaxChars || this.cache("set", "html", html) !== true) {
        this._htmlCacheDisabled = true;
        return;
      }
      this.cache("set", { currentPage: this.currentPage, hasMorePages: this.hasMorePages, url: window.location.href });
    }

    scheduleHtmlCache() {
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));
      this.cancelHtmlCache();
      this._cacheJob = idle(() => { this._cacheJob = null; this.saveHtmlCache(); });
    }

    flushHtmlCache() {
      if (!this._cacheJob) return;
      this.cancelHtmlCache();
      this.saveHtmlCache();
    }

    cancelHtmlCache() {
      if (!this._cacheJob) return;
      (window.cancelIdleCallback || clearTimeout)(this._cacheJob);
      this._cacheJob = null;
    }

    createElementAfter(ref, tag, attrs) {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([key, value]) => (key === "text" ? (el.textContent = value) : key === "onclick" ? el.addEventListener("click", value) : el.setAttribute(key, value)));
      ref.insertAdjacentElement("afterend", el);
      return el;
    }

    createAndAppend(parent, tag, attrs) {
      const el = this.createElementAfter(parent, tag, attrs);
      parent.appendChild(el);
      return el;
    }

    removeLoadingElements() {
      document.querySelectorAll(".infinite-scroll-load-more-wrapper, .infinite-scroll-load-more, .infinite-scroll-loading-image-container, .infinite-scroll-loading-image").forEach((el) => el.remove());
      this.loadMoreWrapper = this.loadMoreBtn = this.loadingImgContainer = this.loadingImg = null;
    }

    yieldToBrowser() {
      // rAF ne conviendrait pas : son callback s'exécute avant le rendu, donc il bloque la frame.
      if (window.scheduler && window.scheduler.yield) return window.scheduler.yield();
      return new Promise(function (resolve) { setTimeout(resolve, 0); });
    }

    isStale(gen) {
      // Caduc si démonté, si un filtre est passé (génération), ou si la grille est détachée.
      return this._disconnected || gen !== this._loadGeneration || !this.productsContainer.isConnected;
    }

    async appendInChunks(children, gen) {
      const nodes = Array.from(children);
      const size = this.options.appendChunkSize;
      // Un DocumentFragment ne marcherait pas : le connectedCallback ne s'y déclenche pas, tout
      // le coût d'init se reconcentrerait sur l'insertion unique.
      const promoted = [];
      for (let i = 0; i < nodes.length; i += size) {
        const end = Math.min(i + size, nodes.length);
        for (let j = i; j < end; j++) {
          const card = nodes[j].cloneNode(true);
          card.classList.add(APPENDING_CLASS);
          const img = this.promoteFirstImage(card);
          if (img) promoted.push(img);
          this.productsContainer.appendChild(card);
        }
        if (end >= nodes.length) break;
        await this.yieldToBrowser();
        if (this.isStale(gen)) { this.revealPending(); return false; }
      }
      await this.waitForImages(promoted, this.options.revealImageTimeout);
      this.revealPending();
      return !this.isStale(gen);
    }

    promoteFirstImage(card) {
      if (!this.options.eagerAppendedImages) return null;
      const img = card.querySelector("img[loading='lazy']");
      if (img) img.setAttribute("loading", "eager");
      return img;
    }

    waitForImages(imgs, timeoutMs) {
      const pending = imgs.filter((img) => img && !img.complete);
      if (!pending.length) return Promise.resolve();
      return Promise.race([
        Promise.all(pending.map((img) => new Promise((resolve) => {
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true }); // une image cassée ne bloque pas
        }))),
        new Promise((resolve) => setTimeout(resolve, timeoutMs)),
      ]);
    }

    restoreLazyLoading() {
      if (!this.options.eagerAppendedImages || !this.productsContainer) return;
      // Pas d'eager dans le snapshot : la grille entière est réinjectée d'un coup au retour.
      const imgs = this.productsContainer.querySelectorAll("img[loading='eager']");
      for (let i = 0; i < imgs.length; i++) imgs[i].setAttribute("loading", "lazy");
    }

    cleanSwiperTraces() {
      // Un slider initialisé laisse ses classes d'état dans le HTML mis en cache, et son init
      // écarte ce qui porte swiper-initialized : sans nettoyage la carte revient inerte.
      const GARDER = /^(swiper-wrapper|swiper-slide|swiper-button-next|swiper-button-prev|swiper-scrollbar)$/;
      const inits = this.productsContainer.querySelectorAll(".swiper-initialized");
      for (let i = 0; i < inits.length; i++) {
        const noeuds = [inits[i]].concat(Array.prototype.slice.call(inits[i].querySelectorAll("*")));
        for (let j = 0; j < noeuds.length; j++) {
          const n = noeuds[j];
          for (let k = n.classList.length - 1; k >= 0; k--) {
            const c = n.classList[k];
            if (c.indexOf("swiper-") === 0 && !GARDER.test(c)) n.classList.remove(c);
          }
          if (n.classList.contains("swiper-wrapper") || n.classList.contains("swiper-slide")) {
            n.removeAttribute("style");
          }
        }
      }
      return inits.length;
    }

    revealPending() {
      if (!this.productsContainer) return 0;
      // Aussi avant chaque écriture du cache : une classe de masquage sérialisée ferait revenir
      // une grille de produits invisibles.
      const marked = this.productsContainer.querySelectorAll("." + APPENDING_CLASS);
      for (let i = 0; i < marked.length; i++) marked[i].classList.remove(APPENDING_CLASS);
      return marked.length;
    }

    async fetchDocument(pageNumber, useSection) {
      const startUrl = window.location.href;
      const url = new URL(startUrl);
      url.searchParams.set("page", pageNumber);
      const sectionId = useSection && this.productsContainer.getAttribute("data-id");
      if (sectionId) url.searchParams.set("section_id", sectionId);
      const gen = this._loadGeneration;
      const t0 = performance.now();
      const res = await fetch(url.toString(), { credentials: "same-origin" });
      if (!res.ok) return null;
      const html = await res.text();
      if (this.isStale(gen) || window.location.href !== startUrl) return null;
      const t1 = performance.now();
      const doc = new DOMParser().parseFromString(html, "text/html");
      return { doc, bytes: html.length, network: t1 - t0, parse: performance.now() - t1, section: !!sectionId };
    }

    async fetchPage(pageNumber) {
      const gen = this._loadGeneration;
      let res = await this.fetchDocument(pageNumber, this.options.useSectionRendering);
      if (!res) return false;
      let container = res.doc.querySelector(this.options.productsContainer);
      if (!container && res.section) {
        this.options.useSectionRendering = false;
        res = await this.fetchDocument(pageNumber, false);
        if (!res) return false;
        container = res.doc.querySelector(this.options.productsContainer);
      }
      if (!container?.children.length) return false;
      const t2 = performance.now();
      if (!(await this.appendInChunks(container.children, gen))) return false;
      if (window.InfiniteScrollDebug) {
        console.log(
          "[infinite-scroll] page " + pageNumber + (res.section ? " (section)" : " (page complete)") +
          " : reseau " + Math.round(res.network) + "ms" +
          ", parse " + Math.round(res.parse) + "ms (" + Math.round(res.bytes / 1024) + "Ko)" +
          ", insertion " + Math.round(performance.now() - t2) + "ms par paquets de " + this.options.appendChunkSize
        );
      }
      this.loadedProductsCount = this.productsContainer.querySelectorAll(this.options.productSelector).length;
      this.currentPage = pageNumber;
      this.hasMorePages = this.checkHasMorePages(res.doc);
      this.lastProductsSignature = this.getProductsSignature();
      return true;
    }

    async loadNextPage() {
      // Verrou posé avant le premier await : deux scrolls dans la même tâche ne passent pas.
      if (this.isLoading || !this.hasMorePages) return;
      this.isLoading = true;
      const gen = this._loadGeneration;
      this.updateLoadingState(true);
      try {
        if (!(await this.fetchPage(this.currentPage + 1))) return this.handleEndOfResults();
        if (!this.hasMorePages) this.handleEndOfResults();
        this.rememberTargetPage(); // avant le HTML : minuscule, donc jamais refusé par le quota
        this.scheduleHtmlCache();
      } catch {
        this.handleEndOfResults();
      } finally {
        this.revealPending(); // filet : une exception ne doit pas laisser de carte invisible
        if (gen === this._loadGeneration) {
          this.isLoading = false;
          this.updateLoadingState(false);
        }
      }
    }

    checkHasMorePages(doc) {
      const container = doc.querySelector(this.options.productsContainer);
      return container?.children.length > 0 && !!doc.querySelector(`a[href*="page=${this.currentPage + 1}"], .pagination a[rel="next"], .pagination .next:not(.disabled)`);
    }

    updateLoadingState(isLoading) {
      if (this.loadMoreBtn) this.loadMoreBtn.disabled = isLoading;
      if (this.loadMoreWrapper) this.loadMoreWrapper.style.display = isLoading ? "none" : "flex";
      if (this.loadingImgContainer) this.loadingImgContainer.style.display = isLoading ? "flex" : "none";
      if (this.loadingImg) this.loadingImg.style.display = isLoading ? "block" : "none";
      if (this.productCountText && this.totalProductsCount > 0) this.productCountText.textContent = `${this.loadedProductsCount} products loaded out of ${this.totalProductsCount}`;
      if (this.progressBar && this.totalProductsCount > 0) this.progressBar.style.width = `${(this.loadedProductsCount / this.totalProductsCount) * 100}%`;
    }

    handleEndOfResults() {
      this.hasMorePages = false;
      this.removeLoadingElements();
    }

    cache(action, key, value) {
      const formatKey = (s) => this.options.storagePrefix + s;
      const serialize = (val) => typeof val === "object" ? JSON.stringify(val) : String(val);
      const deserialize = (val) => {try
        {
          return val ? JSON.parse(val) : null;
        } catch {
          return val;
        }
      };
      try {
        if (action === "get") {
          if (!key) {
            const vals = Object.fromEntries(["url", "html", "hasMorePages", "currentPage", "scrollUrl"].map((k) => [k, deserialize(sessionStorage.getItem(formatKey(k)))]));
            return vals.url && vals.html ? vals : null;
          }
          return deserialize(sessionStorage.getItem(formatKey(key)));
        }
        if (action === "set") {
          if (typeof key === "object") Object.entries(key).forEach(([k, val]) => sessionStorage.setItem(formatKey(k), serialize(val)));
          else sessionStorage.setItem(formatKey(key), serialize(value));
          return true; // écriture confirmée : saveHtmlCache y détecte le quota
        }
        if (action === "clear") Object.keys(sessionStorage).filter((k) => k.startsWith(this.options.storagePrefix)).forEach((k) => sessionStorage.removeItem(k));
      } catch {
        if (action === "set") return false;
      }
      return null;
    }

    observeGridChanges() {
      if (!this.productsContainer) return;
      if (this.gridObserver) this.gridObserver.disconnect();
      this.lastProductsSignature = this.getProductsSignature();
      this.gridObserver = new MutationObserver(() => this.onGridMutation());
      this.gridObserver.observe(this.productsContainer, { childList: true });
    }

    onGridMutation() {
      if (this.isLoading) return;
      if (this.getProductsSignature() === this.lastProductsSignature) return; // bruit tiers
      if (window.location.pathname.includes("/collections/")) this.handleFilterChange();
      this.lastProductsSignature = this.getProductsSignature(); // grille fraîche après reinit
    }

    getProductsSignature() {
      const products = this.productsContainer ? this.productsContainer.querySelectorAll(this.options.productSelector) : [];
      if (!products.length) return "0";
      const idOf = (el) => (el.querySelector('a[href*="/products/"]')?.getAttribute("href") || el.getAttribute("data-product-id") || el.id || "");
      return products.length + "|" + idOf(products[0]) + "|" + idOf(products[products.length - 1]);
    }

    handleFilterChange() {
      this.cancelHtmlCache(); // aucune écriture en attente ne doit atterrir après le clear
      this.cache("clear"); // efface aussi la position mémorisée
      this._htmlCacheDisabled = false; // grille repartie de zéro : le budget sessionStorage est libre
      this._loadGeneration++; // invalide tout chargement en vol : il visait l'ancienne grille
      this.isLoading = false;
      this.initializeState(false);
      this.setupUI();
    }

    disconnectedCallback() {
      this._disconnected = true; // stoppe un rattrapage en cours (voir fetchPage)
      if (this.boundCheckScroll) window.removeEventListener("scroll", this.boundCheckScroll);
      if (this.boundSaveOnHide) window.removeEventListener("pagehide", this.boundSaveOnHide);
      if (this.boundSaveOnHidden) document.removeEventListener("visibilitychange", this.boundSaveOnHidden);
      if (this.boundHandlePageShow) window.removeEventListener("pageshow", this.boundHandlePageShow);
      this.cancelHtmlCache();
      if (this.gridObserver) this.gridObserver.disconnect();
    }
  }
  customElements.define("infinite-scroll", InfiniteScrollComponent);
}